import { useSuspenseQuery } from "@apollo/client";
import { Button, PrimaryFullScreenSpinner } from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { DateTime } from "luxon";
import { Suspense } from "react";
import { CurrencyRateUI } from "~/components/Amount/CurrencyRateUI";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { graphql } from "~/gql/client";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/products_/$productId/unit-prices",
)({
  head: () => opsHead("Product Unit Prices"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const UtcDateTimeUI = ({ at }: { at: Date }) => {
  const utcDateTime = DateTime.fromJSDate(at).setZone("UTC");

  return (
    <div className="flex-1 space-x-1 text-sm">
      <div className="inline-flex items-baseline font-semibold text-sm">
        {utcDateTime.toFormat("yyyy-MM-dd")}
      </div>
      <div className="inline-flex items-baseline text-sm">
        {utcDateTime.toFormat("HH:mm")}
      </div>
      <div className="inline-flex items-baseline text-xs">
        {utcDateTime.toFormat("ZZZZ")}
      </div>
    </div>
  );
};

function View() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminProductUnitPrices($productId: ID!) {
        product(id: $productId) {
          id
          ...on FundProduct {
            unitPriceHistory {
              id
              currency
              unitPrices {
                targetUnit
                at
                price
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        productId: params.productId ?? "",
      },
    },
  );

  if (!data.product || data.product.__typename !== "FundProduct") {
    throw new NotFoundError();
  }

  const unitPriceHistory = data.product.unitPriceHistory;
  if (!unitPriceHistory) {
    throw new Error("expected unit price history to be non-null");
  }

  const unitPriceHistoryItems = unitPriceHistory.unitPrices ?? [];
  const latestUnitPrice = unitPriceHistoryItems[0];

  if (!latestUnitPrice) {
    return <div className="mt-4">No unit price history for this product.</div>;
  }

  return (
    <div>
      <DetailRow label="Latest price">
        <DetailSubRow
          label="Value"
          value={
            <CurrencyRateUI
              currency={unitPriceHistory.currency}
              amount={latestUnitPrice.price}
            />
          }
        />
        <DetailSubRow
          label="As Of"
          value={<UtcDateTimeUI at={latestUnitPrice.at} />}
        />
      </DetailRow>

      <DetailRow label="History (latest 20)">
        <div className="flex w-full flex-col gap-2">
          {unitPriceHistoryItems.slice(0, 20).map((item, idx) => (
            <div
              key={`${item.at.toISOString()}-${idx}`}
              className="flex items-center justify-between border-b pb-1 text-sm"
            >
              <UtcDateTimeUI at={item.at} />
              <CurrencyRateUI
                currency={unitPriceHistory.currency}
                amount={item.price}
              />
            </div>
          ))}
        </div>
      </DetailRow>

      <div>
        <Button
          type="button"
          variant="link"
          onClick={() =>
            navigate({
              to: `/products/${params.productId}/unit-prices/update`,
            })
          }
        >
          Update unit price data
        </Button>
      </div>
    </div>
  );
}
